import React, { useEffect, useRef, useState } from "react";
import { Button } from "antd";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTimes } from "@fortawesome/free-solid-svg-icons";
import { useSelector, useDispatch } from "react-redux";
import { RootState } from "../../../app/store";
import { updateOpenGlobalEndpointPanel } from "../../../redux/monitoring/monitoringSlice";
import { EndpointMessagesProvider } from "./EndpointMessagesContext";
import TerminalEndpointTable from "./TerminalEndpointTable";

const MIN_HEIGHT = 150;
const MAX_HEIGHT = 800;
const DEFAULT_HEIGHT = 320;
const HEADER_OVERHEAD = 125;

export default function GlobalEndpointPanel() {
  const dispatch = useDispatch();
  const openGlobalEndpointPanel = useSelector(
    (state: RootState) => state.monitoring.openGlobalEndpointPanel,
  );

  const [panelHeight, setPanelHeight] = useState(DEFAULT_HEIGHT);
  const panelHeightRef = useRef(DEFAULT_HEIGHT);
  const dragStartY = useRef(0);
  const dragStartHeight = useRef(DEFAULT_HEIGHT);
  const isDragging = useRef(false);

  // Keep ref in sync so drag handlers always read current height
  useEffect(() => {
    panelHeightRef.current = panelHeight;
  }, [panelHeight]);

  // Push page content up by setting a CSS custom property on the document root.
  // Layout.tsx reads this to add bottom padding to the content area.
  useEffect(() => {
    if (openGlobalEndpointPanel) {
      document.documentElement.style.setProperty("--endpoint-panel-height", `${panelHeight}px`);
    }
    return () => {
      document.documentElement.style.setProperty("--endpoint-panel-height", "0px");
    };
  }, [openGlobalEndpointPanel, panelHeight]);

  const handleDragMouseDown = (e: React.MouseEvent) => {
    isDragging.current = true;
    dragStartY.current = e.clientY;
    dragStartHeight.current = panelHeightRef.current;

    const onMouseMove = (moveEvent: MouseEvent) => {
      if (!isDragging.current) return;
      const delta = dragStartY.current - moveEvent.clientY;
      const newHeight = Math.min(MAX_HEIGHT, Math.max(MIN_HEIGHT, dragStartHeight.current + delta));
      setPanelHeight(newHeight);
      panelHeightRef.current = newHeight;
    };

    const onMouseUp = () => {
      isDragging.current = false;
      document.removeEventListener("mousemove", onMouseMove);
      document.removeEventListener("mouseup", onMouseUp);
    };

    document.addEventListener("mousemove", onMouseMove);
    document.addEventListener("mouseup", onMouseUp);
  };

  if (!openGlobalEndpointPanel) return null;

  const tableScrollY = Math.max(50, panelHeight - HEADER_OVERHEAD);

  return (
    <div
      style={{
        position: "fixed",
        bottom: 0,
        left: 0,
        right: 0,
        height: panelHeight,
        zIndex: 100,
        background: "#1f1f1f",
        borderTop: "1px solid #444",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <div
        role="button"
        tabIndex={0}
        aria-label="Drag to resize panel"
        onKeyDown={() => undefined}
        onMouseDown={handleDragMouseDown}
        style={{
          height: 6,
          cursor: "ns-resize",
          background: "#444",
          flexShrink: 0,
        }}
      />
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          padding: "4px 12px",
          borderBottom: "1px solid #444",
          background: "#2a2a2a",
          flexShrink: 0,
        }}
      >
        <span style={{ fontWeight: 600, fontSize: 13 }}>Endpoint Monitor</span>
        <Button
          size="small"
          type="text"
          icon={<FontAwesomeIcon icon={faTimes} />}
          onClick={() => dispatch(updateOpenGlobalEndpointPanel(false))}
          aria-label="Close endpoint monitor"
        />
      </div>
      <div style={{ flex: 1, overflow: "hidden", padding: "4px 8px" }}>
        <EndpointMessagesProvider>
          <TerminalEndpointTable scrollY={tableScrollY} />
        </EndpointMessagesProvider>
      </div>
    </div>
  );
}
